/**
 * Pre-launch gate.
 *
 * While enabled, the public cannot get past the landing page — any attempt to
 * reach /app is sent back to the landing page. This is a soft "coming soon"
 * gate for beta, NOT a security boundary (real protection is Firebase auth +
 * the backend). It exists so visitors don't hit half-finished flows before launch.
 *
 * IMPORTANT — why this keys off the live hostname:
 *   This decision MUST be made at runtime from window.location.hostname, not from
 *   build-time env flags. Build flags (VITE_STAGING_MODE / import.meta.env.DEV)
 *   are constants that the bundler can fold, which previously caused the whole
 *   gate (including the founder bypass) to be tree-shaken away and the live site
 *   to render unlocked. Hostname is a runtime value, so the production lockdown
 *   can never be optimized out.
 *
 * Behavior:
 *  - The founder can always get in on their own browser by visiting
 *    /app?founder=<token> once (remembered in that browser).
 *  - Production hosts (chronovela.app etc.) are ALWAYS locked down.
 *  - Everything else (staging preview channels, localhost) stays open so
 *    bug-fixing and development keep working.
 *  - VITE_LAUNCH_GATE=false can additionally force the gate off everywhere.
 */

/** Flip this to false at launch to open the app for everyone (or set VITE_LAUNCH_GATE=false). */
const DEFAULT_GATE_ENABLED = true;

const FOUNDER_BYPASS_STORAGE_KEY = 'cv_founder_access';
const FOUNDER_BYPASS_TOKEN = 'skaldhaven';

/** Public production hosts that must stay locked until launch. */
const PRODUCTION_HOSTS = [
  'chronovela.app',
  'www.chronovela.app',
  'the-scribe-fecc8.web.app',
  'the-scribe-fecc8.firebaseapp.com',
];

function isTrue(value: unknown): boolean {
  return String(value).toLowerCase() === 'true';
}

/** Runtime check: is this page being served from a locked production host? */
export function isProductionHost(): boolean {
  if (typeof window === 'undefined') return false;
  return PRODUCTION_HOSTS.includes(window.location.hostname.toLowerCase());
}

/** Whether the pre-launch gate is configured on. Env var overrides the default. */
export function isLaunchGateEnabled(): boolean {
  const raw = import.meta.env.VITE_LAUNCH_GATE;
  if (raw === undefined || raw === '') return DEFAULT_GATE_ENABLED;
  return isTrue(raw);
}

/** True if this browser has been granted founder access (via /app?founder=<token>). */
export function hasFounderBypass(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const token = new URLSearchParams(window.location.search).get('founder');
    if (token === FOUNDER_BYPASS_TOKEN) {
      window.localStorage.setItem(FOUNDER_BYPASS_STORAGE_KEY, 'true');
      return true;
    }
    return window.localStorage.getItem(FOUNDER_BYPASS_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Final decision: should app access be blocked for this visitor right now? */
export function isAppAccessBlocked(): boolean {
  // Founder always gets in, on any host.
  if (hasFounderBypass()) return false;

  // If the launch gate has been explicitly turned off, the app is open everywhere.
  if (!isLaunchGateEnabled()) return false;

  // Production hosts are hard-locked at runtime regardless of build flags.
  if (isProductionHost()) return true;

  // Anywhere else (staging preview channels, localhost) stays open.
  return false;
}
